import { type DayOfWeek, type PaymentFrequency, type PaymentScheduleModel, type SemiMonthlyOption, DAY_OF_WEEK } from '@teamworksdev/vuetify';
import { TWICE_A_MONTH_OPTION, type TwiceAMonthOptionEnum as SemiMonthlyOptionEnum, type DayOfWeekEnum, DAY_OF_WEEK as DAY_OF_WEEK_ENUM, FREQUENCY_TYPE_ENUM, type FrequencyTypeEnum, type AgreementStatusEnum, AGREEMENT_STATUS_ENUM } from '@/types/agreements';
import { type PaymentRequestStatus, type WalletStatus, type WalletVerificationStatus, PAYMENT_REQUEST_STATUS_ENUM, WALLET_STATUS_ENUM, WALLET_VERIFICATION_STATUS_ENUM, type ParsedPaymentStatus } from '@/types/paymentRequests';
import { roundToTwoDecimals } from './helpers';
import { ISO_DATE_FORMAT } from './dates';
import { isPast, isValid } from 'date-fns';
import { parse } from 'date-fns';
import Decimal from 'decimal.js';
import { isNil } from 'lodash-es';

/**
 * Utility to calculate evenly distributed payments for a total amount
 * Ensures proper rounding to 2 decimal places and adds any remainder to the last payment
 */
export function calculateDistributedPayments(totalAmount: number, paymentCount: number): number[] {
  if (paymentCount <= 0) return [];

  // Base payment with 2 decimal precision
  const basePayment = paymentCount > 0
    ? Math.floor((totalAmount / paymentCount) * 100) / 100
    : 0;

  // Calculate remainder
  const distributedTotal = basePayment * paymentCount;
  const remainder = Math.round((totalAmount - distributedTotal) * 100) / 100;

  // Create payments array with values
  // Use exactly the same precision as the test expects
  const result = Array(paymentCount).fill(basePayment);

  // Add remainder to last payment and ensure it has exactly 2 decimal places
  if (paymentCount > 0) {
    result[paymentCount - 1] = Number((basePayment + remainder).toFixed(2));
  }

  return result;
}


export const calculateTotalValue = <T = unknown>(values: T[], accessorFn: (item: T) => number): number => {
  if (!values.length) return 0;

  return roundToTwoDecimals(values.reduce((total, item) => total + accessorFn(item), 0));
};

export function parseDateMMDDYYYY(dateStr: string): string {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateStr.trim());
  if (!match) {
    throw new Error(`Invalid date format (expected MM/DD/YYYY): ${dateStr}`);
  }
  const [, month, day, year] = match;
  return `${year}-${month}-${day}`;
}

/**
 * Safely parse a date string in YYYY-MM-DD format to a Date object
 */
export function safeParseDate(dateStr: string): Date | null {
  try {
    // Split the date string into parts
    const [year, month, day] = dateStr.split('-').map(Number) as [number, number, number];
    // Create a new Date object with the parts (month is 0-based)
    const date = new Date(year, month - 1, day);
    // Validate the date
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
}

/**
 * Format a Date object to YYYY-MM-DD string format
 */
export function formatDateOnly(date: Date | null, options?: { format?: 'default' | 'mm/dd/yyyy' }): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (isNaN(date.getTime())) {
    return '';
  }

  switch (options?.format) {
    case 'mm/dd/yyyy':
      return `${month}/${day}/${year}`;
    default:
      return `${year}-${month}-${day}`;
  }
}

/**
 * Check if two arrays of dates match exactly
 */
export function datesMatch(dates1: string[], dates2: string[]): boolean {
  return dates1.length === dates2.length
    && dates1.every((date, index) => date === dates2[index]);
}

// Payment Schedule Utils

const dayOfWeekMapping = [DAY_OF_WEEK.SUNDAY, DAY_OF_WEEK.MONDAY, DAY_OF_WEEK.TUESDAY, DAY_OF_WEEK.WEDNESDAY, DAY_OF_WEEK.THURSDAY, DAY_OF_WEEK.FRIDAY, DAY_OF_WEEK.SATURDAY] as const;

export const getDayOfWeekEnum = (dayOfWeek?: DayOfWeek | null): DayOfWeekEnum | null => {
  switch (dayOfWeek) {
    case 'sunday':
      return DAY_OF_WEEK_ENUM.SUNDAY;
    case 'monday':
      return DAY_OF_WEEK_ENUM.MONDAY;
    case 'tuesday':
      return DAY_OF_WEEK_ENUM.TUESDAY;
    case 'wednesday':
      return DAY_OF_WEEK_ENUM.WEDNESDAY;
    case 'thursday':
      return DAY_OF_WEEK_ENUM.THURSDAY;
    case 'friday':
      return DAY_OF_WEEK_ENUM.FRIDAY;
    case 'saturday':
      return DAY_OF_WEEK_ENUM.SATURDAY;
    default:
      return null;
  }
};

export const getDayOfWeekFromIndex = (index?: number | null): DayOfWeek | null => {
  if (!index) return null;
  return dayOfWeekMapping[index] ?? null;
};

export const getSemiMonthlyOptionIndex = (semiMonthlyOption?: SemiMonthlyOption | null): SemiMonthlyOptionEnum | null => {
  switch (semiMonthlyOption) {
    case 'firstAndFifteenth':
      return TWICE_A_MONTH_OPTION.FIRST_AND_FIFTEENTH;
    case 'fifteenthAndLast':
      return TWICE_A_MONTH_OPTION.FIFTEENTH_AND_LAST;
    default:
      return null;
  }
};

export const getSemiMonthlyOptionFromEnum = (semiMonthlyOptionEnum?: number | null): SemiMonthlyOption | null => {
  switch (semiMonthlyOptionEnum) {
    case TWICE_A_MONTH_OPTION.FIRST_AND_FIFTEENTH:
      return 'firstAndFifteenth';
    case TWICE_A_MONTH_OPTION.FIFTEENTH_AND_LAST:
      return 'fifteenthAndLast';
    default:
      return null;
  }
};

// Map from FREQUENCY_TYPE back to FrequencyTypeEnum
export const mapPaymentScheduleToFrequency = (schedule: PaymentScheduleModel): FrequencyTypeEnum => {
  switch (schedule.frequency) {
    case 'oneTime':
      return FREQUENCY_TYPE_ENUM.ONE_TIME;
    case 'weekly':
      return FREQUENCY_TYPE_ENUM.WEEKLY;
    case 'twiceAMonth':
      return FREQUENCY_TYPE_ENUM.TWICE_A_MONTH;
    case 'monthly':
      return FREQUENCY_TYPE_ENUM.MONTHLY;
    case 'quarterly':
      return FREQUENCY_TYPE_ENUM.QUARTERLY;
    case 'custom':
      return FREQUENCY_TYPE_ENUM.CUSTOM;
    default:
      return FREQUENCY_TYPE_ENUM.MONTHLY;
  }
};

// Convert number to FrequencyTypeEnum
export const numberToFrequencyType = (value: number | null): PaymentFrequency | null => {
  if (value === null) return null;

  switch (value) {
    case FREQUENCY_TYPE_ENUM.ONE_TIME:
      return 'oneTime';
    case FREQUENCY_TYPE_ENUM.WEEKLY:
      return 'weekly';
    case FREQUENCY_TYPE_ENUM.TWICE_A_MONTH:
      return 'twiceAMonth';
    case FREQUENCY_TYPE_ENUM.MONTHLY:
      return 'monthly';
    case FREQUENCY_TYPE_ENUM.QUARTERLY:
      return 'quarterly';
    case FREQUENCY_TYPE_ENUM.CUSTOM:
      return 'custom';
    default:
      return 'monthly';
  }
};

// Payment Request Status Utils


export const getPaymentStatusTranslationKey = (status: PaymentRequestStatus) => {
  switch (status) {
    // Ready
    case PAYMENT_REQUEST_STATUS_ENUM.CREATED:
      return 'paymentRequestStatus.created';
    case PAYMENT_REQUEST_STATUS_ENUM.SENDING:
      return 'paymentRequestStatus.sendingPayment';
    case PAYMENT_REQUEST_STATUS_ENUM.SENT:
      return 'paymentRequestStatus.sent';
    case PAYMENT_REQUEST_STATUS_ENUM.SCHEDULED:
      return 'paymentRequestStatus.scheduled';
    case PAYMENT_REQUEST_STATUS_ENUM.SENDING_FAILED:
      return 'paymentRequestStatus.failedToSend';
    case PAYMENT_REQUEST_STATUS_ENUM.PAID:
      return 'paymentRequestStatus.paid';
    case PAYMENT_REQUEST_STATUS_ENUM.PAID_OUTSIDE_GM:
      return 'paymentRequestStatus.paidOutsideGM';
    case PAYMENT_REQUEST_STATUS_ENUM.CANCELED:
      return 'paymentRequestStatus.canceled';
    default:
      return 'unknown';
  }
};

export const getWalletStatusTranslationKey = (status: WalletStatus) => {
  switch (status) {
    case WALLET_STATUS_ENUM.ACTIVE:
      return 'active';
    case WALLET_STATUS_ENUM.INACTIVE:
      return 'inactive';
    case WALLET_STATUS_ENUM.NOT_FOUND:
      return 'notFound';
    case WALLET_STATUS_ENUM.STATUS_UNAVAILABLE:
      return 'statusUnavailable';
    default:
      return 'unknown';
  }
};

export const getWalletVerificationStatusTranslationKey = (status: WalletVerificationStatus): string => {
  switch (status) {
    case WALLET_VERIFICATION_STATUS_ENUM.ACTIVE:
      return 'walletVerificationStatus.active';
    case WALLET_VERIFICATION_STATUS_ENUM.PENDING_APPLICATION:
      return 'walletVerificationStatus.pendingApplication';
    case WALLET_VERIFICATION_STATUS_ENUM.UNDER_REVIEW:
      return 'walletVerificationStatus.underReview';
    case WALLET_VERIFICATION_STATUS_ENUM.DOCUMENTS_REQUIRED:
      return 'walletVerificationStatus.documentsRequired';
    case WALLET_VERIFICATION_STATUS_ENUM.SUSPENDED:
      return 'walletVerificationStatus.suspended';
    default:
      return '';
  }
};

/**
 *
 * @param {PaymentRequestStatus} status - The payment request status
 * @param {WalletStatus} [walletStatus] - The wallet status
 * @param {string} [paymentDate] - The payment date in YYYY-MM-DD format
 * @returns {ParsedPaymentStatus} The parsed payment status. If walletStatus and paymentDate are provided, it will check if the payment is payable.
 */
export const parsePaymentStatus = (status: PaymentRequestStatus, walletStatus?: WalletStatus, paymentDate?: string, paymentAmount?: number, agreementStatus?: AgreementStatusEnum): ParsedPaymentStatus => {
  let isPayable = true;
  if (walletStatus && paymentDate && !isNil(paymentAmount)) {
    isPayable = isRequestPayable(status, walletStatus, paymentDate, paymentAmount, agreementStatus);
  }

  switch (status) {
    case PAYMENT_REQUEST_STATUS_ENUM.CREATED:
      return isPayable ? 'ready' : 'notPayable';
    case PAYMENT_REQUEST_STATUS_ENUM.SENDING:
      return 'sendingPayment'; // send 20
    case PAYMENT_REQUEST_STATUS_ENUM.SENT:
      return 'sent'; // send 40
    case PAYMENT_REQUEST_STATUS_ENUM.SCHEDULED:
      return 'scheduled'; // send 50
    case PAYMENT_REQUEST_STATUS_ENUM.SENDING_FAILED:
      return 'failedToSend'; // send 60
    case PAYMENT_REQUEST_STATUS_ENUM.PAID:
      return 'paid'; // send 100
    case PAYMENT_REQUEST_STATUS_ENUM.PAID_OUTSIDE_GM:
      return 'paidOutsideGM'; // send 110
    case PAYMENT_REQUEST_STATUS_ENUM.CANCELED:
      return 'canceled'; // send 200
    default:
      return status;
  }
};

/**
 * Checks if the payment date is payable
 *
 * A payment is payable if we haven't gone past 23:59:59.999 UTC on the payment date
 * @param paymentDate - The payment date in YYYY-MM-DD format
 * @returns {boolean} - True if the payment date is payable, false otherwise
 */
export const isPaymentDatePayable = (paymentDate: string): boolean => {
  const parsedDate = parse(paymentDate, ISO_DATE_FORMAT, new Date());

  if (!isValid(parsedDate)) {
    return false;
  }

  const paymentDateEndOfDay = new Date(Date.UTC(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
    23, 59, 59, 999,
  ));

  return !isPast(paymentDateEndOfDay);
};

export const isRequestPayable = (status: PaymentRequestStatus, walletStatus: WalletStatus, paymentDate: string, paymentAmount: number, agreementStatus?: AgreementStatusEnum): boolean => {
  const amountIsGreaterThanOneCent = paymentAmount > 0.01;

  const payableStatuses: PaymentRequestStatus[] = [
    PAYMENT_REQUEST_STATUS_ENUM.CREATED,
    PAYMENT_REQUEST_STATUS_ENUM.SENDING_FAILED,
    PAYMENT_REQUEST_STATUS_ENUM.CANCELED,
  ];
  const isPayableStatus = payableStatuses.includes(status);
  const isActiveWallet = walletStatus === WALLET_STATUS_ENUM.ACTIVE;

  // Only block payments for canceled agreements
  const isNotCanceledAgreement = agreementStatus !== AGREEMENT_STATUS_ENUM.CANCELED;

  return isPayableStatus && isActiveWallet && amountIsGreaterThanOneCent && isPaymentDatePayable(paymentDate) && isNotCanceledAgreement;
};

export const isPaymentEditable = (status: PaymentRequestStatus): boolean => {
  const editableStatuses: PaymentRequestStatus[] = [
    PAYMENT_REQUEST_STATUS_ENUM.CREATED,
    PAYMENT_REQUEST_STATUS_ENUM.SENDING_FAILED,
    PAYMENT_REQUEST_STATUS_ENUM.CANCELED,
    PAYMENT_REQUEST_STATUS_ENUM.PAID_OUTSIDE_GM,
  ];

  return editableStatuses.includes(status);
};

export const isPaymentCancelable = (status: PaymentRequestStatus): boolean => {
  const cancelableStatuses: PaymentRequestStatus[] = [
    PAYMENT_REQUEST_STATUS_ENUM.CREATED,
    PAYMENT_REQUEST_STATUS_ENUM.SENDING_FAILED,
  ];

  return cancelableStatuses.includes(status);
};

/**
 * Checks if a payment request can be selected for bulk actions (Send to Wallet, Set Paid Outside GM, or Revert Paid Outside GM)
 * Payments with status Sent, Scheduled, or Paid cannot be selected
 */
export const isRequestSelectable = (status: PaymentRequestStatus): boolean => {
  const nonSelectableStatuses: PaymentRequestStatus[] = [
    PAYMENT_REQUEST_STATUS_ENUM.SENT,
    PAYMENT_REQUEST_STATUS_ENUM.SCHEDULED,
    PAYMENT_REQUEST_STATUS_ENUM.PAID,
  ];

  return !nonSelectableStatuses.includes(status);
};

/**
 * Checks if a payment request can have its date changed via the bulk update-dates action.
 * Mirrors the server-side editable set of the bulk update-dates endpoint: only Created,
 * Failed to Send, and Canceled payments are accepted (unlike isPaymentEditable, which also
 * allows Paid Outside GM for the single-payment edit).
 */
export const isRequestDateEditable = (status: PaymentRequestStatus): boolean => {
  const dateEditableStatuses: PaymentRequestStatus[] = [
    PAYMENT_REQUEST_STATUS_ENUM.CREATED,
    PAYMENT_REQUEST_STATUS_ENUM.SENDING_FAILED,
    PAYMENT_REQUEST_STATUS_ENUM.CANCELED,
  ];

  return dateEditableStatuses.includes(status);
};

/**
 * Checks if a payment request can be reverted from Paid Outside GM back to Created
 */
export const isPaymentRevertable = (status: PaymentRequestStatus): boolean => {
  return status === PAYMENT_REQUEST_STATUS_ENUM.PAID_OUTSIDE_GM;
};

/**
 * Applies a percentage to a contract value using Decimal.js for precision
 */
export function applyPercentageToValue(value: number, percentage: number): number {
  const valueDecimal = new Decimal(value);
  const percentageDecimal = new Decimal(percentage);
  const result = valueDecimal.times(percentageDecimal).dividedBy(100);
  return result.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toNumber();
}

/**
 * Applies percentage splits to a contract value ensuring the total equals the exact contract value
 * Any rounding remainder is added to the last payment to maintain precision
 */
export function applyPercentageSplitsToValue(
  contractValue: number,
  percentageSplits: { percentage: number }[],
): number[] {
  if (percentageSplits.length === 0) return [];

  const contractDecimal = new Decimal(contractValue);
  const payments: number[] = [];
  let runningTotal = new Decimal(0);

  // Calculate each payment with banker's rounding
  for (const split of percentageSplits) {
    const percentage = new Decimal(split.percentage);
    const payment = contractDecimal.times(percentage).dividedBy(100);
    const roundedPayment = payment.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN);

    payments.push(roundedPayment.toNumber());
    runningTotal = runningTotal.plus(roundedPayment);
  }

  // Calculate any remainder and add it to the last payment
  const remainder = contractDecimal.minus(runningTotal);
  if (!remainder.isZero() && payments.length > 0) {
    const lastIndex = payments.length - 1;
    const lastPayment = new Decimal(payments[lastIndex]!);
    payments[lastIndex] = lastPayment.plus(remainder).toNumber();
  }

  return payments;
}
